import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1565c0', // Deep blue (highway/trust)
      light: '#5e92f3',
      dark: '#003c8f',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#ff6f00', // Orange (caution/visibility)
      light: '#ffa040',
      dark: '#c43e00',
      contrastText: '#ffffff',
    },
    success: {
      main: '#2e7d32', // Green (go/available)
      light: '#60ad5e',
      dark: '#005005',
    },
    warning: {
      main: '#f57c00', // Amber (maintenance)
      light: '#ffad42',
      dark: '#bb4d00',
    },
    error: {
      main: '#c62828', // Red (stop/offline)
      light: '#ff5f52',
      dark: '#8e0000',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
    text: {
      primary: '#212121',
      secondary: '#757575',
    },
  },
  typography: {
    fontFamily: [
      'Roboto',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      letterSpacing: '-0.01562em',
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem',
      letterSpacing: '-0.00833em',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      letterSpacing: '0em',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      letterSpacing: '0.00735em',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      letterSpacing: '0em',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      letterSpacing: '0.0075em',
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: '0.02857em',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
          boxShadow: '0 4px 20px rgba(21, 101, 192, 0.3)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          fontSize: '0.95rem',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
        },
        contained: {
          fontWeight: 600,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          transition: 'box-shadow 0.3s ease-in-out, transform 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        },
        elevation2: {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        },
        elevation3: {
          boxShadow: '0 6px 16px rgba(0, 0, 0, 0.12)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: 6,
        },
        colorSuccess: {
          backgroundColor: '#2e7d32',
          color: '#ffffff',
        },
        colorWarning: {
          backgroundColor: '#f57c00',
          color: '#ffffff',
        },
        colorError: {
          backgroundColor: '#c62828',
          color: '#ffffff',
        },
        colorPrimary: {
          backgroundColor: '#1565c0',
          color: '#ffffff',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#e3f2fd',
          '& .MuiTableCell-head': {
            fontWeight: 700,
            color: '#0d47a1',
            textTransform: 'uppercase',
            fontSize: '0.875rem',
            letterSpacing: '0.05em',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: '#f5f5f5',
          },
          '&:nth-of-type(odd)': {
            backgroundColor: '#fafafa',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '&:hover fieldset': {
              borderColor: '#1565c0',
            },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(21, 101, 192, 0.08)',
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#212121',
          fontSize: '0.875rem',
          borderRadius: 6,
          padding: '8px 12px',
        },
      },
    },
  },
  shadows: [
    'none',
    '0 2px 4px rgba(0,0,0,0.05)',
    '0 2px 8px rgba(0,0,0,0.08)',
    '0 4px 12px rgba(0,0,0,0.1)',
    '0 6px 16px rgba(0,0,0,0.12)',
    '0 8px 20px rgba(0,0,0,0.14)',
    '0 10px 24px rgba(0,0,0,0.16)',
    '0 12px 28px rgba(0,0,0,0.18)',
    '0 14px 32px rgba(0,0,0,0.2)',
    '0 16px 36px rgba(0,0,0,0.22)',
    '0 18px 40px rgba(0,0,0,0.24)',
    '0 20px 44px rgba(0,0,0,0.26)',
    '0 22px 48px rgba(0,0,0,0.28)',
    '0 24px 52px rgba(0,0,0,0.3)',
    '0 26px 56px rgba(0,0,0,0.32)',
    '0 28px 60px rgba(0,0,0,0.34)',
    '0 30px 64px rgba(0,0,0,0.36)',
    '0 32px 68px rgba(0,0,0,0.38)',
    '0 34px 72px rgba(0,0,0,0.4)',
    '0 36px 76px rgba(0,0,0,0.42)',
    '0 38px 80px rgba(0,0,0,0.44)',
    '0 40px 84px rgba(0,0,0,0.46)',
    '0 42px 88px rgba(0,0,0,0.48)',
    '0 44px 92px rgba(0,0,0,0.5)',
    '0 46px 96px rgba(0,0,0,0.52)',
  ],
});

export default theme;
